import moment from "moment-timezone";
import { getDateTokens } from "@/helpers/dateFormat";
import { formatTimezoneAbbr } from "@/helpers/formatTimezoneAbbr";
import { logWarn } from "@/helpers/logError";
import { resolveBookableInstant } from "@/helpers/schedulingTimezone";

/**
 * LSP-16658 / LSP-20627 — the one place the scheduled-call message's date/time placeholders are
 * rendered.
 *
 * Extracted from `ActionSchedule.vue`'s `updateTemplate`, which owned this for the BOOKING flow. The
 * reschedule dialog seeds its message the same way — the org's scheduled template rendered for the
 * newly picked time — so the copy a contact receives after a move is produced by the same mechanism
 * as the original invitation rather than by separate hardcoded wording.
 *
 * ## The invariant (LSP-20627)
 *
 * **No substitution ever reads text that a substitution has already written.** Each call makes at
 * most two left-to-right passes over the incoming message, and neither pass can re-enter its own
 * output:
 *
 * 1. `swapPreviousTokenValues` rewrites the values rendered LAST time into the values for the newly
 *    picked slot. It scans the incoming text with an explicit cursor and appends to a separate output
 *    buffer, so a value it writes is never rescanned.
 * 2. `renderPlaceholders` substitutes the remaining `${...}` placeholders in a single regex pass.
 *
 * Pass 1 runs FIRST and pass 2 second, deliberately: previous rendered values never contain `${`, so
 * pass 1 cannot disturb a placeholder, while running pass 2 first would leave freshly written values
 * lying in the text for pass 1 to match inside.
 *
 * The bug this replaces did the opposite — a chain of single-occurrence `String.replace` calls of
 * each previous value over already-substituted text. Moving 2:00 PM → 12:00 PM found the previous
 * `"2:00 PM"` INSIDE the freshly written `"12:00 PM"` at offset 1 and produced `"112:00 PM"`
 * (likewise 1:00 → 11:00 and 2:30 → 12:30), and single-occurrence `replace` left the second mention
 * of a twice-used token stale. Do not reintroduce either: replacements must cover ALL occurrences,
 * and must never run over text this function itself just wrote.
 *
 * Preserving the user's own edits is why pass 1 exists at all: once a placeholder has been
 * substituted it is gone, so a later slot change can only be applied by finding the previously
 * rendered value in place. Words the user changed around it are untouched. A previous value the user
 * deleted outright is simply not found, and nothing is invented in its place.
 */

/** The rendered values a scheduled-call message substitutes, for one selected date/time/zone. */
export interface ScheduledCallTokens {
	/** "M/DD/YYYY h:mm A ZZZ" — the `${scheduledDate}` token. */
	scheduledDate: string;
	/** Weekday name, the `${day}` token. */
	day: string;
	/** Short date in the org's format, the `${date}` token. */
	date: string;
	/** "h:mm A", the `${time}` token. */
	time: string;
	/** Zone abbreviation, the `${timezone}` token. */
	timezone: string;
}

/** The previously rendered token values, so a slot change can be re-applied without discarding user edits. */
export type PreviousScheduledCallTokens = Partial<ScheduledCallTokens>;

/** One of the slot tokens whose rendered value can be re-applied in place. */
type TokenKey = keyof ScheduledCallTokens;

/** The token keys whose rendered values are re-applied in place when the selected slot changes. */
const REPLACEABLE_TOKEN_KEYS: (keyof ScheduledCallTokens)[] = ["scheduledDate", "day", "date", "time", "timezone"];

/** Matches one `${token}` placeholder. Unknown names are left in place by the renderer below. */
const PLACEHOLDER_PATTERN = /\$\{(\w+)\}/g;

/**
 * Render the tokens for a selected day/time in a zone.
 *
 * `selectedDate` is a tz-naive "YYYY-MM-DD" and `selectedTime` a tz-naive "h:mm A", matching what the
 * pickers produce — deliberately NOT an instant, because the wall-clock the user chose is what the
 * copy must quote. An empty `selectedTime` renders the current clock time, as the booking flow does.
 */
export function renderScheduledCallTokens(options: {
	selectedDate: string;
	selectedTime: string;
	timeZone: string | null | undefined;
	dateFormat: string | null | undefined;
}): ScheduledCallTokens {
	const { selectedDate, dateFormat } = options;
	const zone = options.timeZone ?? moment.tz.guess();

	// ONE moment, in the selected zone, and every token below is read off it.
	//
	// LSP-17619: resolved THROUGH the selected zone, not the browser's. The round-trip is a no-op for
	// a wall clock that exists; its purpose is that moment normalizes one that does NOT — a DST
	// spring-forward gap — into the following hour. Which hours are missing depends on the zone doing
	// the parsing, so a bare `moment()` normalized against the BROWSER's gaps: the invitation named
	// 2:30 AM for a call booked at 3:30 AM (America/New_York, 2026-03-08) when the scheduler's own
	// zone had no gap that day. It is also the instant `schedule()` submits.
	//
	// The zone ABBREVIATION comes off this same moment, and that is the point of building it first.
	// It used to be derived from `moment(selectedDate)` — midnight of the selected day, read in the
	// BROWSER's zone — which names the offset in force at midnight rather than at the booked time.
	// On a DST transition day those differ for the whole rest of the day: a 3:00 AM Chicago booking
	// on 2026-03-08 is CDT, but midnight that day is still CST, so the invitation told the customer
	// CST — an hour wrong, in every browser timezone, not just cross-zone ones.
	const bookedMoment =
		options.selectedTime === ""
			? // No slot picked yet (reset() and mounted() both render the message in this state). Read
				// "now" IN THE SELECTED ZONE: the string is stamped with that zone's abbreviation, so
				// taking the clock from the browser instead would label one zone's wall time with
				// another's name — the same mismatch this function exists to avoid.
				moment().tz(zone)
			: // Routed through the SHARED resolver (helpers/schedulingTimezone.ts) so the abbreviation and
				// the time this copy quotes name the same occurrence schedule() submits. Without it, inside a
				// DST fall-back overlap the customer is told EDT for a booking whose startAt is EST.
				resolveBookableInstant(
					moment.tz(
						`${moment(selectedDate, "YYYY-MM-DD").format("MM/DD/YYYY")} ${options.selectedTime}`,
						"MM/DD/YYYY h:mm A",
						// STRICT (the `true`), matching formatCrossTimezoneHint, which documents why at
						// length: moment's default leniency turns junk into a confident-looking answer. Here
						// it was worse than the "Invalid date" the review flagged — a lenient parse ignores
						// an unreadable TIME entirely and keeps the date, so "not a time" became 12:00 AM and
						// the customer was told, precisely and wrongly, that their call is at midnight. A bad
						// DATE did go invalid and render "Invalid date", which is how the milder half showed
						// up first. One rule, two places in one file, one of them fixed — again.
						true,
						zone
					)
				);

	// AI review: without this, an unparseable date or time renders the literal string "Invalid date"
	// into `time` and `scheduledDate` — copy that goes to a CUSTOMER by SMS and email. Both submit
	// paths now refuse an invalid slot (ActionSchedule's past-slot guard, RescheduleCallDialog's
	// isValid), so this only ever shows in the live preview; falling back to the zone's current clock
	// is exactly what the no-slot-picked branch above already does, and it keeps the preview readable
	// while the field is mid-edit. It IS a time nobody picked, which is why it is logged rather than
	// swallowed: reaching here means a caller produced a slot string its own renderer cannot read.
	const resolvedMoment = bookedMoment.isValid() ? bookedMoment : moment().tz(zone);
	if (!bookedMoment.isValid()) {
		logWarn("[LSP-17619] Scheduled-call tokens fell back to the current clock: unparseable slot", {
			selectedDate: selectedDate,
			selectedTime: options.selectedTime,
			timeZone: zone
		});
	}

	const time = resolvedMoment.format("h:mm A");
	const timezone = formatTimezoneAbbr(resolvedMoment.format("z"));

	// The three date tokens are built from `selectedDate` rather than off `resolvedMoment`, and
	// deliberately so: with no slot picked yet, `resolvedMoment` is the CURRENT clock, and reading the
	// day from it would print today's date under a future day the user has already chosen. What they
	// must not do is print "Invalid date" when selectedDate itself is unreadable, which is the half of
	// the review finding the strict-parse change above does not cover — an unparseable date fails the
	// composed parse, but these three never consulted it.
	const parsedSelectedDate = moment(selectedDate, "YYYY-MM-DD");
	const dateMoment = parsedSelectedDate.isValid() ? parsedSelectedDate : resolvedMoment;

	const dateTokens = getDateTokens(dateFormat);
	return {
		scheduledDate: `${dateMoment.format(dateTokens.scheduledDatePart)} ${time} ${timezone}`,
		day: dateMoment.format("dddd"),
		date: dateMoment.format(dateTokens.dateShort),
		time: time,
		timezone: timezone
	};
}

/**
 * Pass 1 — rewrite every occurrence of a previously rendered value into its new value.
 *
 * Single left-to-right scan of `message` with an explicit cursor, appending to a separate output
 * buffer. Candidates are tried longest-previous-value-first so `${scheduledDate}` (which contains the
 * date, time and zone as substrings) is consumed whole rather than being picked apart by its own
 * components. A matched run is consumed from the INPUT and the new value is appended to the OUTPUT,
 * so nothing written can ever be matched again — that is what makes 2:00 PM → 12:00 PM safe.
 */
/**
 * Does `candidate` occur at `index` as a value in its own right, rather than inside a longer run of
 * letters or digits?
 *
 * A bare `startsWith` is not enough, and the failure is not theoretical: the time token is not
 * zero-padded, so a rendered "1:00 PM" is a literal substring of an ordinary "11:00 PM". A user who
 * writes "Office closes at 11:00 PM" and then moves the slot had that rewritten to "13:00 PM" — the
 * scan matches at the SECOND "1". The same shape applies to "CDT" inside "ACDT" and "Thursday"
 * inside "Thursdays".
 *
 * Found by review on the contact-mobile port of this function (LSP-17619) and fixed on both sides
 * together: the mobile copy was written from this one, so leaving this unguarded would have put the
 * same defect back the moment anyone compared them.
 */
function matchesTokenAt(message: string, candidate: string, index: number): boolean {
	if (!message.startsWith(candidate, index)) {
		return false;
	}
	const isWordCharacter = (character: string | undefined) => character !== undefined && /[A-Za-z0-9]/.test(character);
	return !isWordCharacter(message[index - 1]) && !isWordCharacter(message[index + candidate.length]);
}

/** A pass's output plus the slot keys it actually wrote into the message. */
interface TokenPassResult {
	message: string;
	/**
	 * Keys whose value is ours and is on screen after this pass -- written, rewritten in place, or
	 * found unchanged. Ownership is READ from here, never re-derived from the text afterwards.
	 */
	wrote: Set<TokenKey>;
}

function swapPreviousTokenValues(message: string, previous: PreviousScheduledCallTokens, tokens: ScheduledCallTokens): TokenPassResult {
	// An UNCHANGED value is a candidate too, which it did not used to be. Substituting it is a no-op
	// on the text, but matching it is how this function reports that our value is still on screen --
	// and that is the only precise answer to "do we still own this key". Filtering unchanged values
	// out meant a pass where the slot did not move reported nothing, so the caller either disowned a
	// value still sitting in the message (and then stopped refreshing it) or guessed with a substring
	// probe. Word-boundary matching is already this function's job; asking it is cheaper than
	// approximating it elsewhere.
	const candidates = REPLACEABLE_TOKEN_KEYS.map((key) => ({ key, previousValue: previous[key] ?? "", currentValue: tokens[key] }))
		.filter((candidate) => candidate.previousValue !== "")
		.sort((left, right) => right.previousValue.length - left.previousValue.length);

	const wrote = new Set<TokenKey>();
	if (candidates.length === 0) {
		return { message, wrote };
	}

	let output = "";
	let cursor = 0;
	while (cursor < message.length) {
		const matched = candidates.find((candidate) => matchesTokenAt(message, candidate.previousValue, cursor));
		if (matched) {
			output += matched.currentValue;
			cursor += matched.previousValue.length;
			wrote.add(matched.key);
		} else {
			output += message[cursor];
			cursor += 1;
		}
	}
	return { message: output, wrote };
}

/**
 * Pass 2 — substitute every `${...}` placeholder in ONE regex pass.
 *
 * One pass means a value containing something that looks like a placeholder (an organization named
 * "${time} Inc.", say) is never re-entered, and an unknown `${token}` is left exactly as written
 * rather than being blanked.
 */
function renderPlaceholders(
	message: string,
	tokens: ScheduledCallTokens,
	names: { teamMemberName: string; organizationName: string }
): TokenPassResult {
	const values: Record<string, string> = {
		teamMember: names.teamMemberName,
		user: names.teamMemberName,
		organization: names.organizationName,
		day: tokens.day,
		date: tokens.date,
		time: tokens.time,
		timezone: tokens.timezone,
		scheduledDate: tokens.scheduledDate
	};
	// Recorded as it substitutes, because this function is the only thing that knows which
	// placeholders were actually present. ${teamMember}, ${user} and ${organization} are filled here
	// too but are NOT slot values, so they never enter `wrote` -- a name-only template writes no slot
	// value, and the caller must not read it as though it had.
	const wrote = new Set<TokenKey>();
	const rendered = message.replace(PLACEHOLDER_PATTERN, (placeholder, name: string) => {
		if (!Object.prototype.hasOwnProperty.call(values, name)) {
			return placeholder;
		}
		if ((REPLACEABLE_TOKEN_KEYS as string[]).includes(name)) {
			wrote.add(name as TokenKey);
		}
		return values[name];
	});
	return { message: rendered, wrote };
}

/**
 * Re-apply the previously rendered slot values, then substitute any remaining placeholders.
 *
 * `message` is whatever the field currently holds: the raw org template on the first render, or the
 * last render plus the user's edits on every later one. Returns the new text and the tokens that
 * produced it, which the caller stores and passes back as `previous` on the next call. Pure — it
 * holds no state of its own.
 */
export function applyScheduledCallMessageTokens(
	message: string,
	tokens: ScheduledCallTokens,
	names: { teamMemberName: string; organizationName: string },
	previous: PreviousScheduledCallTokens = {}
): { message: string; tokens: PreviousScheduledCallTokens } {
	const swap = swapPreviousTokenValues(message, previous, tokens);
	const render = renderPlaceholders(swap.message, tokens, names);

	// Ownership is READ from what the two passes reported writing, not re-derived from the text
	// afterwards (Bugbot, three rounds on this one guard). Each earlier attempt inferred it: first
	// "did the whole message change", which a ${teamMember} fill satisfies without writing any slot
	// value; then substring probes for `${key}` and for the previous value, which approximate the
	// swap's own word-boundary rule instead of asking it. The passes already know exactly which keys
	// they touched, so they now say so and this function stops guessing.
	//
	// Two clauses, both reported, none inferred. A key is ours if this call rendered its placeholder,
	// or if the swap found our value in the message -- whether it had to rewrite it or found it
	// already current. Anything else, including a value the user has deleted, is not ours.
	const claimed: PreviousScheduledCallTokens = {};
	for (const key of REPLACEABLE_TOKEN_KEYS) {
		if (render.wrote.has(key) || swap.wrote.has(key)) {
			claimed[key] = tokens[key];
		}
	}
	return { message: render.message, tokens: claimed };
}
